import { useCallback } from 'react';

/**
 * Custom hook for handling keyboard navigation in table cells
 * @param {Array} rows - Table rows
 * @param {boolean} splitMode - Whether split mode is enabled
 * @param {Array} columns - Table columns
 * @param {Array} calculateColumnWidths - Column width calculations
 * @param {Object} headerRef - Header reference
 * @param {Object} scrollableRef - Scrollable container reference
 * @returns {Function} - Key down handler function
 */
export function useTableNavigation(rows, splitMode, columns, calculateColumnWidths, headerRef, scrollableRef) {
  const handleKeyDown = useCallback((e, originalIndex, colIndex, part) => {
    const scrollAndFocus = (input) => {
      if (!input) return;

      input.focus();
      input.select();

      const scrollCellIntoView = (element) => {
        if (!element) return;

        const container = scrollableRef.current;
        if (!container) return;

        const headerHeight = headerRef.current?.offsetHeight || 0;
        const leftStickyWidth = calculateColumnWidths[0] || 0;

        const elRect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        const isAbove = elRect.top < containerRect.top + headerHeight;
        const isBelow = elRect.bottom > containerRect.bottom;
        const isLeft = elRect.left < containerRect.left + leftStickyWidth;
        const isRight = elRect.right > containerRect.right;

        if (isAbove) {
          container.scrollTop -= (containerRect.top + headerHeight - elRect.top);
        } else if (isBelow) {
          container.scrollTop += (elRect.bottom - containerRect.bottom);
        }

        if (isLeft) {
          container.scrollLeft -= (containerRect.left + leftStickyWidth - elRect.left);
        } else if (isRight) {
          container.scrollLeft += (elRect.right - containerRect.right);
        }
      };

      requestAnimationFrame(() => {
        scrollCellIntoView(input);
      });
    };

    if (e.key === 'Enter' || e.key === 'ArrowDown') {
      e.preventDefault();
      const nextRowIndex = originalIndex + 1;
      if (nextRowIndex < rows.length) {
        const nextInputId = `input-${nextRowIndex}-${colIndex}-${part}`;
        const nextInput = document.getElementById(nextInputId);
        scrollAndFocus(nextInput);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevRowIndex = originalIndex - 1;
      if (prevRowIndex >= 0) {
        const prevInputId = `input-${prevRowIndex}-${colIndex}-${part}`;
        const prevInput = document.getElementById(prevInputId);
        scrollAndFocus(prevInput);
      }
    } else if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      if (splitMode && part === 0 && colIndex !== 0) {
        const nextInputId = `input-${originalIndex}-${colIndex}-1`;
        const nextInput = document.getElementById(nextInputId);
        if (nextInput && !nextInput.disabled) {
          scrollAndFocus(nextInput);
          return;
        }
      }
      
      let nextColIndex = colIndex + 1;
      if (nextColIndex < columns.length) {
        const nextInputId = `input-${originalIndex}-${nextColIndex}-0`;
        const nextInput = document.getElementById(nextInputId);
        scrollAndFocus(nextInput);
      }
    } else if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      if (splitMode && part === 1 && colIndex !== 0) {
        const prevInputId = `input-${originalIndex}-${colIndex}-0`;
        const prevInput = document.getElementById(prevInputId);
        if (prevInput) {
          scrollAndFocus(prevInput);
          return;
        }
      }
      
      let prevColIndex = colIndex - 1;
      if (prevColIndex >= 0) {
        const targetPart = splitMode && prevColIndex !== 0 ? 1 : 0;
        const prevInputId = `input-${originalIndex}-${prevColIndex}-${targetPart}`;
        const prevInput = document.getElementById(prevInputId);
        scrollAndFocus(prevInput);
      }
    }
  }, [rows.length, splitMode, columns.length, calculateColumnWidths, headerRef, scrollableRef]);

  return handleKeyDown;
}
